import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { quotesApi } from '../services/api/quotes';
import type { CargoQuoteRequest, QuoteResponseItem } from '../types/quotes';
import { differenceInDays } from 'date-fns/differenceInDays';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { quoteKeys } from './quoteKeys';
import { formatQuoteRequestDate } from '../lib/format';

function getStatusBadgeVariant(status: string): "default" | "destructive" | "outline" | "secondary" {
  switch (status) {
    case 'pending': return 'secondary';
    case 'quoted': return 'default';
    case 'awaiting_contact': return 'outline';
    case 'contacted': return 'default';
    case 'rejected': return 'destructive';
    default: return 'default';
  }
}

function getStatusBadgeClass(status: string): string {
  switch (status) {
    case 'awaiting_contact': return 'bg-sky-50 text-sky-700 border-sky-200';
    case 'contacted': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    default: return '';
  }
}

function sortResponsesByRecency(responses: QuoteResponseItem[]): QuoteResponseItem[] {
  return [...responses].sort((a, b) => {
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
}

function isExpiringSoon(validUntil: string): boolean {
  const daysUntilExpiry = differenceInDays(new Date(validUntil), new Date());
  return daysUntilExpiry >= 0 && daysUntilExpiry <= 7;
}

function isExpired(validUntil: string): boolean {
  return new Date(validUntil) < new Date();
}

export interface UseQuoteDetailReturn {
  quote: CargoQuoteRequest | null;
  loading: boolean;
  imageModalOpen: boolean;
  setImageModalOpen: (open: boolean) => void;
  setCurrentImageIndex: (index: number) => void;
  currentImageIndex: number;
  contactingId: number | null;
  downloadingResponseId: number | null;
  expandedIds: Set<number>;
  toggleExpanded: (responseId: number) => void;
  nextImage: () => void;
  prevImage: () => void;
  handleContactCarrier: (responseId: number) => void;
  handleDownloadPdf: (responseId: number) => Promise<void>;
  pickupDate: string | null;
  orderAge: string;
  companiesResponded: number;
  companiesContacted: number;
  pendingCount: number;
  sortedResponses: QuoteResponseItem[];
  getStatusBadgeVariant: (status: string) => "default" | "destructive" | "outline" | "secondary";
  getStatusBadgeClass: (status: string) => string;
  isExpiringSoon: (validUntil: string) => boolean;
  isExpired: (validUntil: string) => boolean;
}

export function useQuoteDetail(id: string): UseQuoteDetailReturn {
  const { t, i18n } = useTranslation();
  const language = i18n?.resolvedLanguage || i18n?.language;
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [contactingId, setContactingId] = useState<number | null>(null);
  const [downloadingResponseId, setDownloadingResponseId] = useState<number | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const queryClient = useQueryClient();

  // ---- TanStack Query for server state ----
  const { data, isPending } = useQuery({
    queryKey: quoteKeys.detail(id),
    queryFn: () => quotesApi.getQuote(id),
    enabled: !!id,
  });

  const quote = data?.cargo_quote_request ?? null;

  // ---- Mutations (TanStack Query) ----

  const contactCarrierMutation = useMutation({
    mutationFn: (responseId: number) => quotesApi.contactCarrier(id, String(responseId)),
    onSuccess: () => {
      toast.success(t('quote.detail.toasts.contact_carrier_success'));
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
    onError: (err: unknown) => {
      toast.error(
        err instanceof Error ? err.message : t('quote.detail.toasts.contact_carrier_failed')
      );
    },
    onSettled: () => {
      setContactingId(null);
    },
  });

  // ---- Computed values ----
  const sortedResponses = useMemo(() => {
    if (!quote) return [];
    return sortResponsesByRecency(quote.quote_responses);
  }, [quote]);

  // All proposals start collapsed; the client expands them on demand.
  const toggleExpanded = (responseId: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(responseId)) {
        next.delete(responseId);
      } else {
        next.add(responseId);
      }
      return next;
    });
  };

  const nextImage = () => {
    if (quote && quote.cargo_images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % quote.cargo_images.length);
    }
  };

  const prevImage = () => {
    if (quote && quote.cargo_images.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + quote.cargo_images.length) % quote.cargo_images.length);
    }
  };

  const handleContactCarrier = (responseId: number) => {
    if (!quote || !id) return;
    setContactingId(responseId);
    contactCarrierMutation.mutate(responseId);
  };

  const handleDownloadPdf = async (responseId: number) => {
    if (!quote || downloadingResponseId) return;

    setDownloadingResponseId(responseId);
    try {
      const blob = await quotesApi.downloadProposalPdf(quote.id, responseId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${quote.tracking_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const errorKey = error instanceof Error ? error.message : 'quote.detail.toasts.download_pdf_failed';
      toast.error(t(errorKey));
    } finally {
      setDownloadingResponseId(null);
    }
  };

  // Header pickup date keeps its precision in the active language: exact day
  // for a fixed request, month + year for a flexible (month-only) one, nothing
  // when unspecified. Depends on the language so a switch recomputes it.
  const pickupDate = useMemo(
    () =>
      formatQuoteRequestDate(
        quote?.date_type,
        quote?.date_type === 'fixed' ? quote?.origin_fixed_date : quote?.origin_date,
        { language },
      ),
    [quote, language],
  );

  const orderAge = useMemo(() => {
    if (!quote) return '';
    return formatDistanceToNow(new Date(quote.created_at), { addSuffix: true });
  }, [quote]);

  const companiesResponded = useMemo(() => {
    return quote?.response_count ?? quote?.quote_responses.length ?? 0;
  }, [quote]);

  const companiesContacted = useMemo(() => {
    return quote?.companies_contacted ?? 0;
  }, [quote]);

  const pendingCount = useMemo(() => {
    return Math.max(0, companiesContacted - companiesResponded);
  }, [companiesContacted, companiesResponded]);

  return {
    quote,
    loading: isPending,
    imageModalOpen,
    setImageModalOpen,
    setCurrentImageIndex,
    currentImageIndex,
    contactingId,
    downloadingResponseId,
    expandedIds,
    toggleExpanded,
    nextImage,
    prevImage,
    handleContactCarrier,
    handleDownloadPdf,
    pickupDate,
    orderAge,
    companiesResponded,
    companiesContacted,
    pendingCount,
    sortedResponses,
    getStatusBadgeVariant,
    getStatusBadgeClass,
    isExpiringSoon,
    isExpired,
  };
}
